import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLazyQuery, useMutation } from '@apollo/client';
import { CheckboxInput, Dialog, DialogContent, Loading } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import FileDownloadOutlinedIcon from '@guildeducationinc/recess/icons/FileDownloadOutlinedIcon';
import FileUploadOutlinedIcon from '@guildeducationinc/recess/icons/FileUploadOutlinedIcon';
import { AddChildClients } from '@src/components/Admin/AddChildClients';
import { AddClient } from '@src/components/Admin/AddClient';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { DOWNLOAD_CLIENT_CONFIGURATIONS, UPLOAD_CLIENT_CONFIGURATIONS, } from '@src/graphql/queries/client';
import { useAuth } from '@src/hooks/useAuth';
import { useClients } from '@src/hooks/useClients';
import { MinusCircleIcon } from 'lucide-react';
import { Confirm, DataTable, FileUpload, findLocaleByCode, getBaseUrl, Messages, REMOVE_CLIENT_FROM_PARENT, SearchFilterBar, TrackableButton, useAlert, useLocale, useSessionState, } from 'pe-core-frontend';
import { useCallback, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { EditClientModal } from '../EditClientModal';
import styles from './Clients.module.scss';
export const Clients = (props) => {
    const { t, getCurrencySymbol } = useLocale();
    const { isClientAdmin, currentUser } = useAuth();
    const location = useLocation();
    const [searchText, setSearchText] = useSessionState('clientsSearch', '');
    const [showActive, setShowActive] = useSessionState('clientsShowActive', true);
    const [showInactive, setShowInactive] = useSessionState('clientsShowInactive', true);
    /**
     * Determines the active filter for clients.
     * - undefined: show all clients
     * - true: show active only
     * - false: show inactive only
     */
    const getActiveFilter = (showActive, showInactive) => {
        if (showActive === showInactive)
            return undefined; // both true or both false = show all
        return showActive; // one is true, return which one
    };
    const isActiveFilter = getActiveFilter(showActive, showInactive);
    const { clients, orderBy, orderByDir, setOrderBy, setOrderByDir, page, pageSize, setPage, setPageSize, total, refetch, } = useClients({
        parentId: props.parentId,
        excludeChildren: props.excludeChildren,
        searchText: searchText || undefined,
        isActive: isActiveFilter,
    });
    const { showAlert } = useAlert();
    const getClient = useCallback((id) => {
        return clients.find((c) => c.id === id) || null;
    }, [clients]);
    const [selectedClientId, setSelectedClientId] = useState(props.clientId ?? null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clientToRemove, setClientToRemove] = useState(null);
    const [removeClientFromParent] = useMutation(REMOVE_CLIENT_FROM_PARENT);
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleEditClient = (clientId) => {
        if (clientId) {
            setShowEditDialog(true);
            setSelectedClientId(clientId);
        }
    };
    const [downloadClientConfigurationsQuery] = useLazyQuery(DOWNLOAD_CLIENT_CONFIGURATIONS);
    const handleDownloadClientConfigurations = async (clientId) => {
        if (clientId) {
            try {
                setLoading(true);
                const response = await downloadClientConfigurationsQuery({
                    variables: { clientId },
                });
                if (response.data?.downloadClientConfigurations) {
                    window.open(response.data?.downloadClientConfigurations);
                }
            }
            finally {
                setLoading(false);
            }
        }
    };
    const handleUploadClientConfigurations = (clientId) => {
        if (clientId) {
            setShowUploadDialog(true);
            setSelectedClientId(clientId);
        }
    };
    const [uploadClientConfigurationsQuery] = useLazyQuery(UPLOAD_CLIENT_CONFIGURATIONS);
    const handleUpload = async (s3Key, file) => {
        if (s3Key && file) {
            const response = await uploadClientConfigurationsQuery({
                variables: {
                    s3Key,
                    clientId: selectedClientId,
                },
            });
            if (response.data?.uploadClientConfigurations) {
                setMessages(response.data?.uploadClientConfigurations?.messages);
            }
        }
    };
    const handleRemoveClient = async () => {
        if (!clientToRemove) {
            return;
        }
        try {
            await removeClientFromParent({
                variables: { clientId: clientToRemove },
            });
            await refetch();
            setClientToRemove(null);
            showAlert(t('Client removed'));
        }
        catch (e) {
            showAlert(t('Error removing client'));
            console.error(e);
        }
    };
    if (isClientAdmin) {
        return _jsx(Navigate, { to: `/admin/clients/${currentUser?.clientId}` });
    }
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: props.pageTitle || t('Clients'), actions: props.parentId ? (_jsx(AddChildClients, { parentId: props.parentId, onClose: () => refetch() })) : (_jsx(AddClient, { onSave: refetch })), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, enableFilter: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: t('Search by Name'), filterActions: _jsxs("div", { className: styles.filterActions, children: [_jsx("div", { className: styles.filterLabel, children: t('Status') }), _jsx(CheckboxInput, { label: t('Active'), checked: showActive, onChange: (e) => {
                                    setShowActive(e.target.checked);
                                    setPage(1);
                                } }), _jsx(CheckboxInput, { label: t('Inactive'), checked: showInactive, onChange: (e) => {
                                    setShowInactive(e.target.checked);
                                    setPage(1);
                                } })] }), isFilterActive: !(showActive && showInactive) }) }), _jsxs(Loading, { label: '', loaded: !loading, overlay: true, showChildrenWhileLoading: true, children: [_jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, disableColumnSelector: true, rows: clients, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                            setPage(pagination.page + 1);
                            setPageSize(pagination.pageSize);
                        }, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], columns: [
                            {
                                field: 'name',
                                headerName: t('Client Name'),
                                flex: 0.2,
                            },
                            {
                                field: 'code',
                                headerName: t('Client URL'),
                                valueGetter: e => e.row.code || '-',
                                flex: 0.4,
                                renderCell: ({ value: client_code }) => {
                                    const baseUrl = `${getBaseUrl(false)}/${client_code}`;
                                    return (_jsx("a", { href: `${baseUrl}`, target: '_blank', rel: 'noreferrer', children: `${baseUrl}` }));
                                },
                            },
                            {
                                field: 'country',
                                headerName: t('Client Country'),
                                valueGetter: e => e.row.countryName || '-',
                                flex: 0.25,
                            },
                            {
                                field: 'timezone',
                                headerName: t('Client Timezone'),
                                valueGetter: e => e.row.clientConfig?.timezone || '-',
                                flex: 0.2,
                            },
                            {
                                field: 'locale',
                                headerName: t('Default Language'),
                                valueGetter: e => {
                                    if (!e.row.clientConfig?.locale)
                                        return '-';
                                    const locale = findLocaleByCode(e.row.clientConfig.locale);
                                    return locale?.name || e.row.clientConfig.locale;
                                },
                                flex: 0.2,
                            },
                            {
                                field: 'currency',
                                headerName: t('Client Currency'),
                                valueGetter: e => {
                                    if (!e.row.clientConfig?.currency)
                                        return '-';
                                    return `${e.row.clientConfig.currency} (${getCurrencySymbol(e.row.clientConfig.currency) || '?'})`;
                                },
                                width: 100,
                            },
                            {
                                field: 'isActive',
                                headerName: t('Active?'),
                                width: 100,
                                renderCell: ({ value }) => (value ? t('Yes') : t('No')),
                            },
                            {
                                field: 'isParent',
                                headerName: t('Is Parent?'),
                                width: 100,
                                renderCell: ({ value }) => (value ? t('Yes') : t('No')),
                            },
                            {
                                field: 'parentName',
                                headerName: t('Parent?'),
                                width: 150,
                                valueGetter: e => e.row.parentName || '-',
                            },
                            {
                                field: 'action',
                                headerName: t('Actions'),
                                renderCell: ({ row }) => (_jsx("div", { className: styles.actions, children: props.parentId ? (_jsx(TrackableButton, { btnType: 'icon', enText: 'Remove Child Client', tag: `client_${row.id}`, appearance: 'minimal', label: t('Remove Child Client'), icon: _jsx(MinusCircleIcon, { size: 20 }), onClick: () => setClientToRemove(row.id) })) : (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `client_${row.id}`, appearance: 'minimal', label: t('Edit'), icon: _jsx(EditIcon, { size: 20 }), onClick: () => handleEditClient(row.id) }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Download Configurations', tag: `client_${row.id}`, appearance: 'minimal', label: t('Download Configurations'), icon: _jsx(FileDownloadOutlinedIcon, { size: 20 }), onClick: () => handleDownloadClientConfigurations(row.id) }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Upload Configurations', tag: `client_${row.id}`, appearance: 'minimal', label: t('Upload Configurations'), icon: _jsx(FileUploadOutlinedIcon, { size: 20 }), onClick: () => handleUploadClientConfigurations(row.id) }), _jsx(Link, { to: `${location.pathname}/${row.id}`, children: t('Manage Client') })] })) })),
                                flex: 0.2,
                                sortable: false,
                            },
                        ] }), (showEditDialog && selectedClientId && getClient(selectedClientId) && (_jsx(EditClientModal, { client: getClient(selectedClientId), onCancel: () => {
                            setSelectedClientId(null);
                            setShowEditDialog(false);
                        }, onSave: () => {
                            setSelectedClientId(null);
                            setShowEditDialog(false);
                            refetch();
                        } }))) ||
                        null, _jsx(Dialog, { isOpen: showUploadDialog, onClose: () => {
                            setSelectedClientId(null);
                            setShowUploadDialog(false);
                            setMessages([]);
                        }, children: _jsxs(DialogContent, { children: [_jsx("div", { className: 'text-3xl', children: t('Upload Client Configurations') }), _jsx("div", { className: 'my-3', children: _jsx(Messages, { messages: messages }) }), _jsx("div", { children: _jsx(FileUpload, { label: '', hideLabel: true, fileType: 'json', s3KeyPrefix: 'client-configs', onUploaded: handleUpload, buttonLabel: t('Upload JSON File') }) })] }) }), clientToRemove && (_jsx(Confirm, { message: t('Are you sure you want to remove this client from the parent?'), onConfirm: handleRemoveClient, onCancel: () => setClientToRemove(null) }))] })] }));
};
